import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PortalFooter from '../components/PortalFooter';
import CallbackWidget from '../components/CallbackWidget';
import PublicNavBar from '../components/PublicNavBar';
import { contentAPI } from '../services/api';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EuroIcon from '@mui/icons-material/Euro';
import GroupIcon from '@mui/icons-material/Group';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';



const FEATURES = [
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: 'Schnelle Vermittlung',
    description:
      'Innerhalb von 24 Stunden erhalten Sie Kontakt von geprüften Call-Center-Dienstleistern, die exakt zu Ihren Anforderungen passen.',
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: 'Persönlich geprüft',
    description:
      '230+ verifizierte Call-Center-Partner mit nachgewiesener Qualität, langjähriger Erfahrung und vollständiger DSGVO-Compliance.',
  },
  {
    icon: <EuroIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: '100 % Kostenlos',
    description:
      'Keine versteckten Gebühren für Auftraggeber. Das Stellen einer Anfrage ist völlig kostenlos und absolut unverbindlich.',
  },
  {
    icon: <GroupIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: 'Max. 4 Angebote',
    description:
      'Sie werden von maximal 4 Dienstleistern kontaktiert – so behalten Sie die Übersicht und vermeiden Angebots-Flut.',
  },
  {
    icon: <PhoneIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: 'Alle Branchen',
    description:
      'Ob Inbound, Outbound, Telesales oder Kundensupport – wir finden den richtigen Partner für jede Branche und Aufgabe.',
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 40, color: '#667eea' }} />,
    title: 'DSGVO-konform',
    description:
      'Alle Partner sind DSGVO-zertifiziert. Ihre Daten werden ausschließlich für die Vermittlung verwendet und nicht weiterverkauft.',
  },
];

const STEPS_CUSTOMER = [
  {
    step: '01',
    title: 'Anfrage ausfüllen',
    description: 'In 2 Minuten Ihre Anforderungen beschreiben – Branche, Umfang, Budget und Zeitraum.',
  },
  {
    step: '02',
    title: 'Prüfung & Freigabe',
    description: 'Unser Team prüft Ihre Anfrage und veröffentlicht das Gesuch an geeignete Partner.',
  },
  {
    step: '03',
    title: 'Angebote erhalten',
    description: 'Bis zu 4 geprüfte Call-Center-Dienstleister kontaktieren Sie direkt per Telefon oder E-Mail.',
  },
  {
    step: '04',
    title: 'Auftrag vergeben',
    description: 'Vergleichen Sie die Angebote und wählen Sie den besten Partner – ohne Verpflichtungen.',
  },
];


const TESTIMONIALS = [
  {
    name: 'Thomas M.',
    role: 'Geschäftsführer, E-Commerce',
    text: 'Innerhalb von 48 Stunden hatten wir drei konkrete Angebote auf dem Tisch. Der Prozess war unkompliziert und schnell.',
    rating: 5,
  },
  {
    name: 'Sandra K.',
    role: 'Marketingleiterin, Versicherung',
    text: 'Callcenter-Suche hat uns geholfen, genau den richtigen Outbound-Partner für unsere Kampagne zu finden. Sehr empfehlenswert!',
    rating: 5,
  },
  {
    name: 'Michael B.',
    role: 'Inhaber, Call Center',
    text: 'Als Dienstleister schätzen wir die Qualität der Leads. Die Kunden sind gut informiert und die Abschlussrate ist überdurchschnittlich hoch.',
    rating: 5,
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [siteContent, setSiteContent] = useState<Record<string, string>>({});

  useEffect(() => {
    contentAPI.getAll()
      .then((res) => setSiteContent(res.data.data || {}))
      .catch(() => {});
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <PublicNavBar />

      <section id="hero">
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 35%, #0f3460 65%, #533483 100%)',
            color: '#fff',
            py: { xs: 10, md: 16 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-40%', left: '-20%',
              width: '70%', height: '180%',
              background: 'radial-gradient(ellipse, rgba(232,93,38,0.15) 0%, transparent 65%)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-20%', right: '-10%',
              width: '60%', height: '120%',
              background: 'radial-gradient(ellipse, rgba(102,126,234,0.2) 0%, transparent 65%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="✓ 230+ geprüfte Call-Center-Partner"
              sx={{
                mb: 4,
                backgroundColor: 'rgba(232,93,38,0.2)',
                color: '#ffb899',
                fontWeight: 700,
                border: '1px solid rgba(232,93,38,0.4)',
                fontSize: '0.8rem',
                letterSpacing: '0.02em',
              }}
            />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 900,
                mb: 3,
                fontSize: { xs: '2.2rem', md: '3.2rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Das Vermittlungsportal für{' '}
              <Box component="span" sx={{ color: '#E85D26' }}>
                Call-Center-Dienstleistungen
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 6, opacity: 0.8, fontWeight: 400, maxWidth: 560, mx: 'auto', lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.1rem' }, whiteSpace: 'pre-line' }}
            >
              {siteContent.content_hero || 'Finden Sie in 24 Stunden den passenden Call-Center-Partner – kostenlos, unverbindlich und DSGVO-konform.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: '#E85D26',
                  color: '#fff',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(232,93,38,0.5)',
                  '&:hover': { backgroundColor: '#c94d1e', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(232,93,38,0.5)' },
                  transition: 'all 0.2s ease',
                }}
                onClick={() => navigate('/submit-inquiry')}
              >
                Kostenlose Anfrage stellen
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.4)',
                  color: '#fff',
                  fontWeight: 600,
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 2,
                  backdropFilter: 'blur(4px)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.8)', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
                onClick={() => navigate('/login')}
              >
                Anmelden
              </Button>
            </Stack>
            <Stack direction="row" spacing={4} justifyContent="center" sx={{ mt: 6, flexWrap: 'wrap', gap: 2 }}>
              {[
                { value: '230+', label: 'Partner' },
                { value: '1.500+', label: 'Vermittlungen' },
                { value: '24h', label: 'Ø Antwortzeit' },
                { value: '98%', label: 'Zufriedenheit' },
              ].map((s) => (
                <Box key={s.label} sx={{ textAlign: 'center', px: 2 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#E85D26', lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', opacity: 0.65, mt: 0.3 }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Container>
        </Box>
      </section>

      <section id="features">
        <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
              >
                Ihre Vorteile
              </Typography>
              <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
                Warum Callcenter-Suche?
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {siteContent.content_features || 'Wir verbinden Unternehmen mit den besten Call-Center-Dienstleistern Deutschlands – schnell, sicher und transparent.'}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {FEATURES.map((f) => (
                <Grid key={f.title} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid #f0f0f0',
                      p: 1,
                      borderRadius: 3,
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        borderColor: '#E85D26',
                        boxShadow: '0 12px 40px rgba(232,93,38,0.12)',
                        transform: 'translateY(-6px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          backgroundColor: 'rgba(232,93,38,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2.5,
                        }}
                      >
                        {React.cloneElement(f.icon as React.ReactElement<any>, { sx: { fontSize: 28, color: '#E85D26' } })}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e' }}>
                        {f.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                        {f.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </section>

      <section id="how-it-works">
        <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
              >
                In 4 Schritten zum Ziel
              </Typography>
              <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
                So einfach funktioniert's
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 540, mx: 'auto', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {siteContent.content_how_it_works || 'In wenigen Minuten Ihre Anforderungen beschreiben – wir finden den passenden Call-Center-Partner.'}
              </Typography>
            </Box>

            <Box
              sx={{
                background: 'linear-gradient(135deg, #fff 0%, #fff8f5 100%)',
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                border: '2px solid #fde8de',
                boxShadow: '0 4px 24px rgba(232,93,38,0.06)',
              }}
            >
              <Grid container spacing={3}>
                {STEPS_CUSTOMER.map((s) => (
                  <Grid key={s.step} size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', gap: 2.5 }}>
                      <Box
                        sx={{
                          minWidth: 44,
                          height: 44,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(232,93,38,0.35)',
                        }}
                      >
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>
                          {s.step}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a2e' }}>
                          {s.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                          {s.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 5, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)',
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 6,
                    py: 1.5,
                    boxShadow: '0 4px 16px rgba(232,93,38,0.4)',
                    '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)', transform: 'translateY(-1px)' },
                  }}
                  onClick={() => navigate('/submit-inquiry')}
                >
                  Jetzt kostenlos Anfrage stellen
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </section>

      <section id="testimonials">
        <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{ color: '#E85D26', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
              >
                Kundenstimmen
              </Typography>
              <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#1a1a2e' }}>
                Was unsere Kunden sagen
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 540, mx: 'auto', lineHeight: 1.7 }}>
                Über 1.500 erfolgreiche Vermittlungen – das sagen Auftraggeber und Dienstleister.
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {TESTIMONIALS.map((t) => (
                <Grid key={t.name} size={{ xs: 12, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid #f0f0f0',
                      borderRadius: 3,
                      p: 1,
                      transition: 'all 0.25s ease',
                      '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.08)', transform: 'translateY(-4px)' },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <StarIcon key={i} sx={{ color: '#FFB400', fontSize: 18 }} />
                        ))}
                      </Stack>
                      <Typography
                        variant="body1"
                        sx={{ mb: 3, lineHeight: 1.75, color: '#444', fontStyle: 'italic', fontSize: '0.95rem' }}
                      >
                        „{t.text}"
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', width: 40, height: 40, fontSize: '0.9rem', fontWeight: 700 }}>
                          {t.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                            {t.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {t.role}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </section>

      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%', right: '-10%',
            width: '50%', height: '200%',
            background: 'radial-gradient(ellipse, rgba(232,93,38,0.12) 0%, transparent 65%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 900, color: '#fff', mb: 2, letterSpacing: '-0.02em' }}>
            Bereit, den richtigen Partner zu finden?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, fontWeight: 400, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {siteContent.content_cta || 'Stellen Sie jetzt Ihre kostenlose und unverbindliche Anfrage – in weniger als 2 Minuten.'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#E85D26',
              color: '#fff',
              fontWeight: 700,
              px: 6,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(232,93,38,0.5)',
              '&:hover': { backgroundColor: '#c94d1e', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(232,93,38,0.5)' },
              transition: 'all 0.2s ease',
            }}
            onClick={() => navigate('/submit-inquiry')}
          >
            Kostenlose Anfrage starten
          </Button>
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 5, flexWrap: 'wrap', gap: 2 }}>
            {['Kostenlos für Auftraggeber', 'DSGVO-konform', 'Keine Verpflichtung'].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CheckCircleIcon sx={{ color: '#E85D26', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <PortalFooter />
      <CallbackWidget />
    </Box>
  );
};

export default LandingPage;
